import { Scale, Clock, Users, Award, CheckCircle, ArrowRight } from "lucide-react";

export const WhyChooseUs = () => {
  const differentiators = [
    {
      icon: Scale,
      title: "Attorney-Led Expertise",
      description: "Every transaction benefits from legal oversight. Problems are caught early, complex issues resolved proactively, and you get expertise most agencies cannot match.",
    },
    {
      icon: Award,
      title: "30 Years of Excellence",
      description: "Three decades of institutional knowledge across multiple states and transaction types. We've seen it all and know how to navigate any situation.",
    },
    {
      icon: Users,
      title: "Premier Underwriter Relationships",
      description: "Direct relationships with First American and Fidelity enable pragmatic decisions and exceptions that other agencies may not be able to secure.",
    },
    {
      icon: Clock,
      title: "Proactive Communication",
      description: "We don't wait for problems to surface. You'll hear from us early, often, and transparently, keeping all parties informed throughout.",
    },
  ];

  const features = [
    "Complex transaction specialists",
    "High-value property expertise",
    "Multi-party deal management",
    "Unusual title issue resolution",
    "4-state licensed (PA, NJ, NY, FL)",
    "Same-day response commitment",
  ];

  return (
    <section id="why-us" className="py-24 lg:py-32 bg-[#FAFAF9] noise-bg" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="relative z-10">
            <p className="text-xs font-bold uppercase tracking-widest text-[#B45309] mb-4">Why Choose Us</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-[#0F172A] tracking-tight mb-6">
              The Last Impression Matters Most
            </h2>
            <p className="text-lg text-[#64748B] leading-relaxed mb-8">
              The closing is the final and most memorable touchpoint in every real estate transaction. 
              We position ourselves as the partner that prevents you from "falling over the finish line," ensuring 
              the closing experience reflects well on you.
            </p>

            {/* Feature List */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3" data-testid={`feature-${index}`}>
                  <CheckCircle className="w-4 h-4 text-[#B45309] flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-sm text-[#64748B]">{feature}</span>
                </div>
              ))}
            </div>

            <a
              href="#contact"
              className="inline-flex items-center gap-3 text-[#0F172A] font-bold text-sm uppercase tracking-widest group"
              data-testid="why-us-cta"
            >
              Partner With Us
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </a>
          </div>

          {/* Right - Differentiator Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div
                key={index}
                className="p-6 bg-white border border-[#E7E5E4] card-shadow"
                data-testid={`differentiator-${index}`}
              >
                <div className="w-10 h-10 flex items-center justify-center border border-[#0F172A]/10 mb-4">
                  <item.icon className="w-5 h-5 text-[#0F172A]" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-lg font-medium text-[#0F172A] mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
